import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../../auth/access";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TodayIcon from "@mui/icons-material/Today";
import DownloadIcon from "@mui/icons-material/SaveAlt";
import EditIcon from "@mui/icons-material/EditOutlined";
import { nanoid } from "nanoid";
import type {
  LocalizedString,
  MenuDay,
  MenuFeedResult,
  MenuMeal,
  MenuWeek,
  SchemeContent,
  SchemeMenu,
} from "@shared/types/foyer";
import {
  fromIsoDate,
  isoWeekDates,
  isoWeekParts,
  shiftIsoWeek,
  toIsoDate,
} from "@shared/isoWeek";
import {
  DEFAULT_MEAL_SLOTS,
  copyWeekTo,
  countFilledDays,
  countMeals,
  findWeek,
  mealHasContent,
  mergeWeeks,
  upsertWeek,
} from "@shared/menu";
import { resolveLocalized } from "@shared/i18n";
import LocalizedTextField from "../LocalizedTextField";
import { usePremiseScope } from "../../context/PremiseScopeContext";
import { foyerApi } from "../../http/foyer";
import { ItemFormSurface } from "../EditDrawer";
import type { ContentPaneProps } from "./ContentEditor";
import type { Translate } from "../../i18n/translate";
import { useFormat, type Formatters } from "../../i18n/format";

const FEED_URL_PLACEHOLDER = "https://kommun.se/matsedel/solgarden.rss";

/**
 * Matsedel - the food-menu editor.
 *
 * Two sources, one screen. "Manual" is the week grid below; "Feed" points at
 * the kitchen's RSS and lets the server keep it current. The stored weeks are
 * never discarded when you switch to Feed, so a premise can adopt a feed,
 * discover it is wrong, and switch straight back to what they had written.
 *
 * The week grid is the load-bearing bit: an operator plans a rotation weeks
 * ahead, so the week NUMBER is the primary axis of navigation (it is what the
 * kitchen's paper schedule is keyed on), the seven days are always visible at
 * once, and "duplicate to next week" exists because a menu is almost always a
 * small edit on the previous week rather than a blank page.
 */

const DAY_MONTH: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
};

/** "27 Jul – 2 Aug" for a week's date span. */
function spanLabel(fmt: Formatters, dates: string[]): string {
  const a = fromIsoDate(dates[0]);
  const b = fromIsoDate(dates[6]);
  if (!a || !b) return "";
  return `${fmt.date(a, DAY_MONTH)} – ${fmt.date(b, DAY_MONTH)}`;
}

/** "Monday 3 Aug" for one date of the week. */
function dayLabel(fmt: Formatters, date: string): string {
  const d = fromIsoDate(date);
  if (!d) return date;
  return fmt.date(d, { weekday: "long", ...DAY_MONTH });
}

function weekdayName(fmt: Formatters, date: string, short = false): string {
  const d = fromIsoDate(date);
  return d ? fmt.date(d, { weekday: short ? "short" : "long" }) : date;
}

/** Map a `menu_feed_*` code to something an operator can act on. */
function feedErrorMessage(t: Translate, code: string | undefined): string {
  switch (code) {
    case "menu_feed_bad_url":
      return t("templates:menuEditor.feed.errors.badUrl");
    case "menu_feed_blocked_host":
      return t("templates:menuEditor.feed.errors.blockedHost");
    case "menu_feed_timeout":
      return t("templates:menuEditor.feed.errors.timeout");
    case "menu_feed_unparseable":
      return t("templates:menuEditor.feed.errors.unparseable");
    case "menu_feed_empty":
      return t("templates:menuEditor.feed.errors.empty");
    case "menu_feed_too_large":
      return t("templates:menuEditor.feed.errors.tooLarge");
    default:
      return t("templates:menuEditor.feed.errors.generic");
  }
}

export default function MenuEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const { t } = useTranslation(["templates", "common"]);
  const fmt = useFormat();
  const premiseId = usePremiseScope();
  const menu: SchemeMenu = content.menu ?? {};
  const source = menu.source ?? "manual";
  const slots = menu.mealSlots?.length ? menu.mealSlots : DEFAULT_MEAL_SLOTS;

  const setMenu = (part: Partial<SchemeMenu>) =>
    onChange({ ...content, menu: { ...menu, ...part } } as SchemeContent);

  // ── Week navigation ─────────────────────────────────────────────────────
  const today = useMemo(() => new Date(), []);
  const nowParts = useMemo(() => isoWeekParts(today), [today]);
  const [cursor, setCursor] = useState<{ year: number; week: number }>(
    nowParts
  );

  const dates = useMemo(
    () => isoWeekDates(cursor.year, cursor.week),
    [cursor.year, cursor.week]
  );
  const week = findWeek(menu.weeks, cursor.year, cursor.week);
  const todayIso = toIsoDate(today);
  const isCurrentWeek =
    cursor.year === nowParts.year && cursor.week === nowParts.week;

  /** Write the edited week back, pruning it away entirely if it ends up empty. */
  const writeWeek = (next: MenuWeek) =>
    setMenu({ weeks: upsertWeek(menu.weeks, next) });

  /** The week currently under the cursor, materialised so edits have something
   *  to write into even before anything has been typed. */
  const draftWeek: MenuWeek = week ?? {
    year: cursor.year,
    week: cursor.week,
    days: [],
  };

  const dayFor = (date: string): MenuDay =>
    draftWeek.days.find((d) => d.date === date) ?? { date, meals: [] };

  const setDay = (date: string, meals: MenuMeal[]) => {
    const others = draftWeek.days.filter((d) => d.date !== date);
    writeWeek({
      ...draftWeek,
      days: [...others, { date, meals }].sort((a, b) =>
        a.date.localeCompare(b.date)
      ),
    });
  };

  const addMeal = (date: string, meal: MenuMeal) =>
    setDay(date, [...dayFor(date).meals, meal]);

  // Which serving the modal / drawer is on: `index: "new"` = adding to `date`.
  const [editing, setEditing] = useState<{
    date: string;
    index: number | "new";
  } | null>(null);
  const editingMeal: MenuMeal | undefined = editing
    ? editing.index === "new"
      ? { id: nanoid(8), label: "", body: "" }
      : dayFor(editing.date).meals[editing.index]
    : undefined;

  /** Give a day the premise's full set of serving slots in one click. */
  const fillDayWithSlots = (date: string) => {
    const existing = dayFor(date).meals;
    const have = new Set(
      existing.map((m) =>
        resolveLocalized(m.label, defaultLocale, defaultLocale)
      )
    );
    const additions = slots
      .filter(
        (s) =>
          !have.has(resolveLocalized(s.label, defaultLocale, defaultLocale))
      )
      .map((s) => ({ id: nanoid(8), label: s.label, body: "" }));
    setDay(date, [...existing, ...additions]);
  };

  const weeksWithContent = useMemo(
    () =>
      (menu.weeks ?? [])
        .filter((w) => countMeals(w) > 0)
        .map((w) => ({
          year: w.year,
          week: w.week,
          meals: countMeals(w),
        })),
    [menu.weeks]
  );

  return (
    <Stack spacing={3} data-testid="menu-editor">
      <Box>
        <Typography variant="h6">{t("menuEditor.title")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("menuEditor.intro")}
        </Typography>
      </Box>

      <Tabs
        value={source}
        onChange={(_, v) => setMenu({ source: v as SchemeMenu["source"] })}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          value="manual"
          label={t("menuEditor.sourceManual")}
          data-testid="menu-source-manual"
        />
        <Tab
          value="feed"
          label={t("menuEditor.sourceFeed")}
          data-testid="menu-source-feed"
        />
      </Tabs>

      {source === "feed" && (
        <FeedPanel
          menu={menu}
          premiseId={premiseId}
          onChange={setMenu}
          onAdopt={(weeks) =>
            setMenu({ weeks: mergeWeeks(menu.weeks, weeks), source: "manual" })
          }
        />
      )}

      {source === "feed" && (
        <Alert severity="info">{t("menuEditor.feedFallback")}</Alert>
      )}

      {/* ── Week navigator ─────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              size="small"
              aria-label={t("menuEditor.previousWeek")}
              onClick={() =>
                setCursor(shiftIsoWeek(cursor.year, cursor.week, -1))
              }
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box sx={{ textAlign: "center", minWidth: 190 }}>
              <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
                {t("menuEditor.week", { week: cursor.week })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("menuEditor.weekSpan", {
                  span: spanLabel(fmt, dates),
                  year: cursor.year,
                })}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={t("menuEditor.nextWeek")}
              onClick={() =>
                setCursor(shiftIsoWeek(cursor.year, cursor.week, 1))
              }
            >
              <ChevronRightIcon />
            </IconButton>
            {isCurrentWeek ? (
              <Chip
                size="small"
                color="primary"
                label={t("menuEditor.thisWeek")}
              />
            ) : (
              <Button
                size="small"
                startIcon={<TodayIcon />}
                onClick={() => setCursor(nowParts)}
              >
                {t("menuEditor.thisWeek")}
              </Button>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              size="small"
              variant="outlined"
              label={t("menuEditor.weekStats", {
                days: countFilledDays(week),
                meals: countMeals(week),
              })}
            />
            <Tooltip title={t("menuEditor.duplicateHint")}>
              <span>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  disabled={countMeals(week) === 0}
                  onClick={() => {
                    const next = shiftIsoWeek(cursor.year, cursor.week, 1);
                    setMenu({
                      weeks: upsertWeek(
                        menu.weeks,
                        copyWeekTo(draftWeek, next.year, next.week)
                      ),
                    });
                    setCursor(next);
                  }}
                >
                  {t("menuEditor.duplicate")}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={t("menuEditor.clearHint")}>
              <span>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={countMeals(week) === 0}
                  onClick={() => writeWeek({ ...draftWeek, days: [] })}
                >
                  {t("menuEditor.clearWeek")}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Jump-to chips for every authored week, so "which weeks have I done?"
            is answerable at a glance instead of by paging through the year. */}
        {weeksWithContent.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 1 }}
              >
                {t("menuEditor.authoredWeeks")}
              </Typography>
              {weeksWithContent.map((w) => (
                <Chip
                  key={`${w.year}-${w.week}`}
                  size="small"
                  label={t("menuEditor.weekChip", { week: w.week })}
                  color={
                    w.year === cursor.year && w.week === cursor.week
                      ? "primary"
                      : "default"
                  }
                  variant={
                    w.year === cursor.year && w.week === cursor.week
                      ? "filled"
                      : "outlined"
                  }
                  onClick={() => setCursor({ year: w.year, week: w.week })}
                />
              ))}
            </Stack>
          </>
        )}
      </Paper>

      {/* ── The seven days ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
          gap: 2,
          alignItems: "start",
        }}
      >
        {dates.map((date) => {
          const day = dayFor(date);
          const parsed = fromIsoDate(date);
          const isToday = date === todayIso;
          return (
            <Paper
              key={date}
              variant="outlined"
              data-testid={`menu-day-${date}`}
              sx={{
                p: 2,
                borderColor: isToday ? "primary.main" : undefined,
                borderWidth: isToday ? 2 : 1,
              }}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "fontWeightSemibold" }}
                  >
                    {weekdayName(fmt, date)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const shown = parsed ? fmt.date(parsed, DAY_MONTH) : date;
                      return isToday
                        ? t("menuEditor.dateToday", { date: shown })
                        : shown;
                    })()}
                  </Typography>
                </Box>
                <Badge
                  badgeContent={day.meals.filter(mealHasContent).length}
                  color="primary"
                  showZero={false}
                />
              </Stack>

              <Stack spacing={1} divider={<Divider flexItem />}>
                {day.meals.map((meal, mi) => (
                  <ServingRow
                    key={meal.id}
                    meal={meal}
                    index={mi}
                    defaultLocale={defaultLocale}
                    testId={`menu-serving-${date}-${mi}`}
                    onEdit={() => setEditing({ date, index: mi })}
                    onRemove={() =>
                      setDay(
                        date,
                        day.meals.filter((_, x) => x !== mi)
                      )
                    }
                  />
                ))}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setEditing({ date, index: "new" })}
                  data-testid={`menu-add-serving-${date}`}
                >
                  {t("menuEditor.serving")}
                </Button>
                {day.meals.length === 0 && (
                  <Button size="small" onClick={() => fillDayWithSlots(date)}>
                    {t("menuEditor.useStandard")}
                  </Button>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {editing && editingMeal && (
        <ServingSurface
          key={`${editing.date}-${editing.index}`}
          mode={editing.index === "new" ? "create" : "edit"}
          initial={editingMeal}
          dayLabel={dayLabel(fmt, editing.date)}
          supportedLocales={supportedLocales}
          defaultLocale={defaultLocale}
          onClose={() => setEditing(null)}
          onSave={(meal) => {
            if (editing.index === "new") addMeal(editing.date, meal);
            else
              setDay(
                editing.date,
                dayFor(editing.date).meals.map((m, x) =>
                  x === editing.index ? meal : m
                )
              );
            setEditing(null);
          }}
        />
      )}

      <MealSlotsPanel
        menu={menu}
        supportedLocales={supportedLocales}
        defaultLocale={defaultLocale}
        onChange={setMenu}
      />
    </Stack>
  );
}

// ── One serving ────────────────────────────────────────────────────────────

/** A serving on a day card: what it is and the dish, plus Edit and Remove. */
function ServingRow({
  meal,
  index,
  defaultLocale,
  testId,
  onEdit,
  onRemove,
}: {
  meal: MenuMeal;
  index: number;
  defaultLocale: string;
  testId: string;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const text = (v: LocalizedString | undefined) =>
    resolveLocalized(v, defaultLocale, defaultLocale).trim();
  const label = text(meal.label);
  const body = text(meal.body);
  const note = text(meal.note);
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={0.5}
      data-testid={testId}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label={t("menuEditor.editServingN", { n: index + 1 })}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          py: 0.5,
          px: 0.75,
          mx: -0.75,
          borderRadius: 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Typography variant="subtitle2" noWrap>
          {label || t("menuEditor.servingN", { n: index + 1 })}
        </Typography>
        <Typography
          variant="body2"
          color={body ? "text.primary" : "text.secondary"}
          sx={{ fontStyle: body ? "normal" : "italic" }}
        >
          {body || t("menuEditor.noDish")}
        </Typography>
        {note && (
          <Typography variant="caption" color="text.secondary">
            {note}
          </Typography>
        )}
      </Box>
      <Tooltip title={t("common:buttons.edit")}>
        <IconButton size="small" onClick={onEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        aria-label={t("menuEditor.removeServingN", { n: index + 1 })}
        onClick={onRemove}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

/**
 * Add (modal) / edit (right drawer) one serving. It works on a local copy:
 * Add / Apply writes it into the menu draft - which the page saves later - once
 * the surface has closed; Cancel throws it away.
 */
function ServingSurface({
  mode,
  initial,
  dayLabel: day,
  supportedLocales,
  defaultLocale,
  onClose,
  onSave,
}: {
  mode: "create" | "edit";
  initial: MenuMeal;
  dayLabel: string;
  supportedLocales: string[];
  defaultLocale: string;
  onClose: () => void;
  onSave: (meal: MenuMeal) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [meal, setMeal] = useState<MenuMeal>(initial);
  const loc = { supportedLocales, defaultLocale };
  return (
    <ItemFormSurface
      mode={mode}
      onClose={onClose}
      title={
        mode === "create"
          ? t("menuEditor.addServing")
          : t("menuEditor.editServing")
      }
      subtitle={day}
      data-testid="menu-serving-form"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => dismiss(() => onSave(meal))}
            data-testid="menu-serving-save"
          >
            {mode === "create"
              ? t("common:buttons.add")
              : t("common:buttons.apply")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <LocalizedTextField
          label={t("menuEditor.serving")}
          value={meal.label}
          {...loc}
          placeholder={t("menuEditor.servingPlaceholder")}
          onChange={(label) => setMeal((m) => ({ ...m, label }))}
        />
        <LocalizedTextField
          label={t("menuEditor.dish")}
          value={meal.body}
          {...loc}
          multiline
          rows={2}
          placeholder={t("menuEditor.dishPlaceholder")}
          onChange={(body) => setMeal((m) => ({ ...m, body }))}
        />
        <LocalizedTextField
          label={t("menuEditor.note")}
          value={meal.note}
          {...loc}
          placeholder={t("menuEditor.notePlaceholder")}
          onChange={(note) => setMeal((m) => ({ ...m, note }))}
        />
      </Stack>
    </ItemFormSurface>
  );
}

// ── RSS panel ──────────────────────────────────────────────────────────────

function FeedPanel({
  menu,
  premiseId,
  onChange,
  onAdopt,
}: {
  menu: SchemeMenu;
  premiseId: string | undefined;
  onChange: (part: Partial<SchemeMenu>) => void;
  onAdopt: (weeks: MenuWeek[]) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const fmt = useFormat();
  const [url, setUrl] = useState(menu.feed?.url ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MenuFeedResult | null>(null);

  const test = async () => {
    if (!premiseId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const out = await foyerApi.previewMenuFeed(premiseId, url.trim());
      setResult(out);
      // A successful read is also the operator's intent to use this URL, so
      // store it - they still have to Save the premise for it to take effect.
      onChange({ feed: { ...menu.feed, url: url.trim() } });
    } catch (err) {
      setError(
        feedErrorMessage(t, (err as { code?: string } | undefined)?.code)
      );
    } finally {
      setBusy(false);
    }
  };

  const totalDays = (result?.weeks ?? []).reduce(
    (n, w) => n + w.days.length,
    0
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "fontWeightSemibold" }}
          >
            {t("menuEditor.feed.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("menuEditor.feed.intro")}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            label={t("menuEditor.feed.address")}
            placeholder={FEED_URL_PLACEHOLDER}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            inputProps={{ "data-testid": "menu-feed-url" }}
          />
          <TextField
            size="small"
            select
            label={t("menuEditor.feed.refreshInterval")}
            value={menu.feed?.refreshMinutes ?? 60}
            onChange={(e) =>
              onChange({
                feed: {
                  url: menu.feed?.url ?? url.trim(),
                  refreshMinutes: Number(e.target.value),
                },
              })
            }
            sx={{ minWidth: 140 }}
            SelectProps={{ native: true }}
          >
            <option value={15}>{t("menuEditor.feed.intervals.every15")}</option>
            <option value={60}>{t("menuEditor.feed.intervals.hourly")}</option>
            <option value={360}>
              {t("menuEditor.feed.intervals.every6h")}
            </option>
            <option value={1440}>{t("menuEditor.feed.intervals.daily")}</option>
          </TextField>
          <PermissionGate resource="tv-layout" level="edit">
            <Button
              variant="contained"
              disabled={!url.trim() || busy || !premiseId}
              onClick={test}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
              sx={{ whiteSpace: "nowrap" }}
              data-testid="menu-feed-test"
            >
              {t("menuEditor.feed.test")}
            </Button>
          </PermissionGate>
        </Stack>

        {!premiseId && (
          <Alert severity="info">{t("menuEditor.feed.savePremiseFirst")}</Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {result && (
          <Stack spacing={1.5}>
            <Alert
              severity={totalDays > 0 ? "success" : "warning"}
              action={
                result.weeks.length > 0 ? (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => onAdopt(result.weeks)}
                  >
                    {t("menuEditor.feed.copyIntoWeeks")}
                  </Button>
                ) : undefined
              }
            >
              {totalDays > 0
                ? t(
                    result.feedTitle
                      ? "menuEditor.feed.readFrom"
                      : "menuEditor.feed.read",
                    {
                      days: totalDays,
                      weeks: result.weeks.length,
                      feed: result.feedTitle,
                    }
                  )
                : t("menuEditor.feed.noPlacement")}
            </Alert>

            {result.weeks.map((w) => (
              <Box key={`${w.year}-${w.week}`}>
                <Typography variant="subtitle2">
                  {t("menuEditor.feed.weekHeading", {
                    week: w.week,
                    span: spanLabel(fmt, isoWeekDates(w.year, w.week)),
                  })}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.5 }}
                >
                  {w.days.map((d) => {
                    return (
                      <Tooltip
                        key={d.date}
                        title={d.meals
                          .map(
                            (m) =>
                              `${typeof m.label === "string" ? m.label : ""}: ${typeof m.body === "string" ? m.body : ""}`
                          )
                          .join("\n")}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t("menuEditor.feed.dayChip", {
                            day: weekdayName(fmt, d.date, true),
                            meals: d.meals.length,
                          })}
                        />
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            ))}

            {/* Never silently drop what we did not understand - the operator is
                the only one who can tell whether it mattered. */}
            {result.unplaced.length > 0 && (
              <Alert severity="warning">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "fontWeightSemibold" }}
                >
                  {t("menuEditor.feed.unplaced", {
                    count: result.unplaced.length,
                  })}
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  {t("menuEditor.feed.unplacedHelp")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1 }}
                >
                  {result.unplaced.slice(0, 8).map((u, i) => (
                    <Chip
                      key={i}
                      size="small"
                      variant="outlined"
                      label={
                        u.title ||
                        u.body.slice(0, 40) ||
                        t("menuEditor.feed.untitled")
                      }
                    />
                  ))}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

// ── Serving slots ──────────────────────────────────────────────────────────

/**
 * The premise's standard servings. Purely a labour-saver: "Use standard
 * servings" stamps these onto an empty day so an operator types dishes rather
 * than retyping "Lunch · 12:00-13:00" seven times a week.
 *
 * Deliberately edited in place, unlike a serving: each entry is ONE text field
 * (a name), so a modal or drawer per entry would only add clicks.
 */
function MealSlotsPanel({
  menu,
  supportedLocales,
  defaultLocale,
  onChange,
}: {
  menu: SchemeMenu;
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (part: Partial<SchemeMenu>) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const slots = menu.mealSlots?.length ? menu.mealSlots : DEFAULT_MEAL_SLOTS;
  const write = (next: typeof slots) => onChange({ mealSlots: next });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "fontWeightSemibold" }}>
        {t("menuEditor.standard.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t("menuEditor.standard.intro")}
      </Typography>
      <Stack spacing={1}>
        {slots.map((slot, i) => (
          <Stack key={slot.id} direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <LocalizedTextField
                value={slot.label}
                supportedLocales={supportedLocales}
                defaultLocale={defaultLocale}
                onChange={(label) =>
                  write(slots.map((s, x) => (x === i ? { ...s, label } : s)))
                }
              />
            </Box>
            <IconButton
              size="small"
              aria-label={t("menuEditor.standard.removeN", { n: i + 1 })}
              disabled={slots.length <= 1}
              onClick={() => write(slots.filter((_, x) => x !== i))}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Button
        size="small"
        startIcon={<AddIcon />}
        sx={{ mt: 1 }}
        onClick={() => write([...slots, { id: nanoid(8), label: "" }])}
      >
        {t("menuEditor.standard.add")}
      </Button>
    </Paper>
  );
}

// Re-exported so tests can assert on the copy without duplicating it.
export { feedErrorMessage, spanLabel };
